import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings } from "@phosphor-icons/react";
import { getAttribution } from "@/lib/attribution";
import { trackLead } from "@/lib/fbPixel";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", organization: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const attribution = getAttribution();
      await register(attribution ? { ...form, attribution } : form);
      trackLead();
      navigate("/onboarding");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-zinc-900">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1758473083916-9ebd2ffc3a2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGFic3RyYWN0fGVufDB8fHx8MTc3NjYxNTcxM3ww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover opacity-85"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white flex items-center justify-center">
              <Buildings size={18} color="#1d4ed8" weight="bold" />
            </div>
            <div className="font-display font-black tracking-tight">GRANTFLOW</div>
          </div>
          <div>
            <div className="gf-overline text-white/70">Create your workspace</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-4 max-w-md">
              Win more federal funding.
            </h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-zinc-50">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="register-form">
          <div className="gf-overline">Register</div>
          <h2 className="font-display font-bold text-3xl mt-2 tracking-tight">
            Create your account.
          </h2>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="gf-overline">Full Name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={up("name")}
                className="gf-sharp mt-2 border-zinc-300 bg-white" placeholder="Jane Cole" />
            </div>
            <div>
              <Label className="gf-overline">Organization</Label>
              <Input data-testid="register-org" value={form.organization} onChange={up("organization")}
                className="gf-sharp mt-2 border-zinc-300 bg-white" placeholder="Civic Foundation" />
            </div>
            <div>
              <Label className="gf-overline">Email</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={up("email")}
                className="gf-sharp mt-2 border-zinc-300 bg-white" placeholder="jane@org.gov" />
            </div>
            <div>
              <Label className="gf-overline">Password</Label>
              <Input data-testid="register-password" type="password" required value={form.password} onChange={up("password")}
                className="gf-sharp mt-2 border-zinc-300 bg-white" placeholder="min 6 chars" />
            </div>
            {err && (
              <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy} data-testid="register-submit"
              className="gf-sharp w-full bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
              {busy ? "Creating account…" : "Create account"}
            </Button>
            <div className="text-xs text-zinc-500 font-mono leading-relaxed" data-testid="register-legal-consent">
              By creating an account you agree to our{" "}
              <Link to="/legal/terms" className="underline hover:text-zinc-800">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/legal/privacy" className="underline hover:text-zinc-800">Privacy Policy</Link>.
              Paid plans auto-renew monthly; cancel anytime via the Stripe portal.
            </div>
          </div>
          <div className="mt-6 text-sm text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-700 font-semibold hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
